import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MAT_DATE_LOCALE, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { map, Observable, startWith } from 'rxjs';
import { Organization } from '../../../../organizations/models/Organization';

@Component({
  selector: 'app-reservation-basic-information-step',
  templateUrl: './basic-information-step.component.html',
  styleUrls: ['./basic-information-step.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
  ],
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIcon,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatDatepicker,
    MatInputModule,
    MatAutocompleteModule
  ]
})
export class BasicInformationStepComponent implements OnInit, AfterViewInit {

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @Input('basicInformationForm') form!: FormGroup;
  @Input('organizations') organizations!: Organization[];
  @Output() departureDateEvent = new EventEmitter<number>();
  @Output() selectedOrgIdEvent = new EventEmitter<number>();

  title: string = "Basic Information";
  panelOpen: boolean = false;
  complete: boolean = false;
  error: boolean = false;
  icon: string = "expand_more";

  orgControl = new FormControl('', Validators.required);
  filteredOrganizations!: Observable<any[]>;
  organizationSelect!: Organization | undefined;

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.loadFromReservation();
  }

  ngAfterViewInit() {
    this.filteredOrganizations = this.orgControl.valueChanges.pipe(
      startWith(''),
      map(value => this._orgFilter(value || '')),
    );

    this.autocompleteTrigger.panelClosingActions.subscribe((e) => {      
      if (!(e && e.source)) {
        this.orgControl.setValue('');
        this.form.setErrors({ invalid: true});
        this.autocompleteTrigger.closePanel();
      }
    });
    this.cdr.detectChanges()
  }

  private _orgFilter(value: string): Organization[] {
    return this.organizations.filter(org => org.organization_name.toLowerCase().includes(value.toString().toLowerCase()));
  }

  loadFromReservation() {
    if (this.form.value.organization) {
      this.orgControl.setValue(this.form.value.organization);
      this.organizationSelect = this.form.value.organization;
      this.orgControl.disable();
    } else {
      this.orgControl.enable();
    }
  }

  onOrgSelectionChanged(event: MatAutocompleteSelectedEvent) {
    this.organizationSelect = event.option.value;

    if (this.organizationSelect?.organization_id) {
      this.form.controls['organization'].setValue(this.organizationSelect);
      this.selectedOrgIdEvent.emit(this.organizationSelect?.organization_id);
    }
  }

  displayOrgFn(org: Organization): string {
    return org && org.organization_name ? org.organization_name : '';
  }

  get arrivalDateValue(): Date | null {
    const value = this.form?.get('arrival_date')?.value;
    return value ? new Date(value) : null;
  }

  get departureDateValue(): Date | null {
    const value = this.form?.get('departure_date')?.value;
    return value ? new Date(value) : null;
  }
}